import { Box, BoxProps } from "@vinomofo/components";
import type { PropsWithChildren, RefObject } from "react";
import { forwardRef, useRef, useState } from "react";
import { getPosition, useScroll } from "../../hooks/use-scroll";

import styled from "styled-components";

export interface ScrollingImageContainerProps extends BoxProps {
  scrollOnElementId: string;
}

const Container = styled(Box)`
  transform: translateY(0);
  z-index: -1;
  max-width: 1160px;
  margin: 0 auto;
`;

export const ScrollingImageContainer = forwardRef(
  (
    {
      scrollOnElementId,
      children,
      ...rest
    }: PropsWithChildren<ScrollingImageContainerProps>,
    ref: RefObject<HTMLElement>
  ): JSX.Element => {
    const [scrollOnElement, setScrollOnElement] = useState<Element | null>(
      null
    );
    const [
      heroImageOriginalPos,
      setHeroImageOriginalPos,
    ] = useState<DOMRect | null>(null);

    const heroImageRef = ref;

    useScroll(
      (didResize) => {
        const visible =
          window !== undefined && heroImageRef.current
            ? window
                .getComputedStyle(heroImageRef.current)
                .getPropertyValue("display") !== "none"
            : false;

        if (!visible || !heroImageRef.current) return;
        if (!scrollOnElement) {
          return setScrollOnElement(
            document.querySelector(`#${scrollOnElementId}`)
          );
        }
        if (!heroImageOriginalPos || didResize) {
          return setHeroImageOriginalPos(getPosition(heroImageRef.current));
        }
        const scrollElementPos = getPosition(scrollOnElement);
        const bottomPosition =
          heroImageRef.current.offsetTop + heroImageRef.current.clientHeight;

        if (scrollElementPos && scrollElementPos.top <= bottomPosition) {
          heroImageRef.current.style.transform = `translateY(-${
            bottomPosition - scrollElementPos.top
          }px)`;
        } else {
          heroImageRef.current.style.transform = "translateY(0)";
        }
      },
      [heroImageRef, scrollOnElement, heroImageOriginalPos]
    );

    return (
      <Container left={0} right={0} {...rest} ref={heroImageRef}>
        {children}
      </Container>
    );
  }
);

ScrollingImageContainer.displayName = "ScrollingImageContainer";
